import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Trash2, Plus, Minus, ArrowLeft, ArrowRight, CreditCard, ShieldCheck, Lock, Truck, Sparkles } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import TextScramble from '../components/TextScramble';
import { formatFCFA } from '../lib/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SKIN_TYPE_FR = { 'Oily': 'Grasse', 'Dry': 'S\u00e8che', 'Sensitive': 'Sensible', 'All': 'Tous' };
const FREE_SHIPPING_THRESHOLD = 250000; // FCFA

export default function CartPage() {
  const { items, totalPrice, updateQuantity, removeItem, clearCart, sessionId } = useCart();
  const { customer } = useCustomerAuth();
  const navigate = useNavigate();
  const [checkoutForm, setCheckoutForm] = useState({ customer_name: '', customer_email: '', shipping_address: '', customer_phone: '' });
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [senepayReady, setSenepayReady] = useState(null);
  const [payError, setPayError] = useState('');
  // Iframe modal state — keeps customer on balmarts.com during SenePay checkout
  const [checkoutModal, setCheckoutModal] = useState(null); // { url, orderId }
  const [searchParams] = useSearchParams();
  const recoverOrderId = searchParams.get('recover') || '';
  const promoFromUrl = searchParams.get('promo') || '';
  const refFromUrl = searchParams.get('ref') || '';
  const initialPromo = promoFromUrl || refFromUrl || (typeof window !== 'undefined' ? (localStorage.getItem('balmart_referral_code') || '') : '');
  const [recoveryBanner, setRecoveryBanner] = useState(false);
  const [promoInput, setPromoInput] = useState(initialPromo);
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, discount_percent, discount_amount, new_total }
  const [promoError, setPromoError] = useState('');
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  // Loyalty points redemption
  const [loyalty, setLoyalty] = useState(null); // { points, value_fcfa, point_value_fcfa, min_redeem }
  const [pointsToUse, setPointsToUse] = useState(0);

  useEffect(() => {
    if (items.length > 0) {
      const firstId = items[0]?.product_id;
      axios.get(`${API}/products/suggest`, { params: { product_id: firstId, limit: 3 } })
        .then(r => {
          const existingIds = new Set(items.map(i => i.product_id));
          setSuggestions((r.data || []).filter(p => !existingIds.has(p.product_id)).slice(0, 3));
        })
        .catch(() => setSuggestions([]));
    } else {
      setSuggestions([]);
    }
  }, [items]);

  useEffect(() => {
    if (customer) {
      setCheckoutForm(prev => ({
        customer_name: `${customer.first_name} ${customer.last_name}`,
        customer_email: customer.email,
        shipping_address: customer.address || prev.shipping_address,
        customer_phone: customer.phone || prev.customer_phone,
      }));
    }
  }, [customer]);

  // Fetch loyalty balance for logged-in customers
  useEffect(() => {
    if (!customer) { setLoyalty(null); setPointsToUse(0); return; }
    axios.get(`${API}/customer/loyalty`)
      .then(r => setLoyalty(r.data))
      .catch(() => setLoyalty(null));
  }, [customer, orderPlaced]);

  useEffect(() => {
    const fetchConfig = async (retries = 2) => {
      for (let i = 0; i <= retries; i++) {
        try {
          const r = await axios.get(`${API}/payment/senepay/config`, { timeout: 10000 });
          setSenepayReady(!!r.data?.enabled);
          return;
        } catch (e) {
          if (i === retries) setSenepayReady(false);
          else await new Promise(res => setTimeout(res, 800 * (i + 1)));
        }
      }
    };
    fetchConfig();
  }, []);

  // ⭐ CRITICAL: While SenePay popup/redirect overlay is up, poll backend for order
  // status AND watch the popup. As soon as payment is detected (paid/failed/cancelled)
  // OR the popup is closed, navigate the main window to the success page so the
  // customer sees confirmation instead of staying stuck on the overlay forever.
  useEffect(() => {
    if (!checkoutModal?.orderId) return;
    let cancelled = false;
    const orderId = checkoutModal.orderId;

    const goToSuccess = () => {
      if (cancelled) return;
      cancelled = true;
      setCheckoutModal(null);
      navigate(`/payment-success?order_id=${orderId}`);
    };

    const pollStatus = async () => {
      try {
        const r = await axios.get(`${API}/orders/${orderId}/public`);
        const ps = (r.data?.payment_status || '').toLowerCase();
        if (ps === 'paid' || ps === 'failed' || ps === 'cancelled' || ps === 'expired') {
          goToSuccess();
        }
      } catch { /* ignore and retry */ }
    };

    // Poll order status every 2s
    const statusInterval = setInterval(() => { if (!cancelled) pollStatus(); }, 2000);

    // Watch popup (desktop flow): if customer closes it, assume they're done & redirect
    let popupInterval = null;
    if (checkoutModal.mode === 'popup' && checkoutModal.popup) {
      popupInterval = setInterval(() => {
        try {
          if (checkoutModal.popup.closed) {
            clearInterval(popupInterval);
            // Give backend a moment to receive webhook or for next poll to detect paid
            setTimeout(() => { if (!cancelled) goToSuccess(); }, 1500);
          }
        } catch { /* cross-origin guard */ }
      }, 700);
    }

    return () => {
      cancelled = true;
      clearInterval(statusInterval);
      if (popupInterval) clearInterval(popupInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutModal?.orderId]);

  // Recover an abandoned cart: fetch the old order's items and add them to cart
  useEffect(() => {
    if (!recoverOrderId) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/orders/${recoverOrderId}/public`);
        if (r.data?.payment_status === 'paid') return; // already paid, ignore
        setCheckoutForm(prev => ({
          ...prev,
          customer_name: r.data.customer_name || prev.customer_name,
          customer_email: r.data.customer_email || prev.customer_email,
        }));
        setRecoveryBanner(true);
        setShowCheckout(true);
      } catch {
        // Order not found / expired — ignore
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recoverOrderId]);

  // Auto-apply promo from URL (from recovery email link)
  useEffect(() => {
    if (!promoFromUrl || appliedPromo || totalPrice < 1) return;
    applyPromo(promoFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoFromUrl, totalPrice]);

  const applyPromo = async (codeOverride) => {
    const code = (codeOverride || promoInput || '').trim();
    if (!code) return;
    setValidatingPromo(true);
    setPromoError('');
    try {
      const r = await axios.post(`${API}/promo/validate`, {
        code,
        email: checkoutForm.customer_email || '',
        total: totalPrice,
      });
      setAppliedPromo(r.data);
      setPromoInput(r.data.code);
    } catch (err) {
      setAppliedPromo(null);
      setPromoError(err?.response?.data?.detail || 'Code invalide');
    } finally {
      setValidatingPromo(false);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
    setPromoError('');
  };

  const effectiveTotal = appliedPromo ? appliedPromo.new_total : totalPrice;
  // Compute points discount (clamped client-side; backend caps at 90% of total)
  const pointValue = loyalty?.point_value_fcfa || 0.035;
  const rawPointsDiscount = Math.floor((pointsToUse || 0) * pointValue);
  const maxPointsDiscount = Math.floor(effectiveTotal * 0.9);
  const pointsDiscount = Math.min(rawPointsDiscount, maxPointsDiscount);
  const totalBeforeFees = Math.max(0, effectiveTotal - pointsDiscount);
  // Frais de transaction — 1.8% répercutés au client (facturés par SenePay)
  const TRANSACTION_FEE_RATE = 0.018;
  const transactionFee = Math.round(totalBeforeFees * TRANSACTION_FEE_RATE);
  const finalTotal = totalBeforeFees + transactionFee;

  const placeOrder = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setPayError('');
    try {
      const orderItems = items.map(i => ({ product_id: i.product_id, quantity: i.quantity }));
      const res = await axios.post(`${API}/payment/senepay/initiate`, {
        ...checkoutForm,
        customer_id: customer?.customer_id || '',
        items: orderItems,
        promo_code: appliedPromo?.code || null,
        points_used: pointsToUse || 0,
      });
      if (res.data?.redirect_url) {
        // Engineering solution — Wave's /pay/ page shows either:
        //  - a native deep-link button (opens Wave app) when viewport is a real
        //    top-level mobile browser,
        //  - a QR code when viewport is desktop OR a "mini browser" (popup).
        // So on mobile we MUST use full-page navigation; on desktop a popup with
        // the QR flow is the correct UX.
        const isMobile = /Android|iPhone|iPad|iPod|Mobile|Opera Mini|BlackBerry|webOS/i.test(navigator.userAgent);
        if (isMobile) {
          setCheckoutModal({ url: res.data.redirect_url, orderId: res.data.order_id, amount: finalTotal, mode: 'redirect' });
          setTimeout(() => { window.location.href = res.data.redirect_url; }, 1200);
          return;
        }
        // Desktop: popup window with QR code flow
        const w = 440;
        const h = 760;
        const left = Math.max(0, (window.screen.width - w) / 2);
        const top = Math.max(0, (window.screen.height - h) / 2);
        const features = `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes`;
        const popup = window.open(res.data.redirect_url, 'balmart_pay', features);
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
          setCheckoutModal({ url: res.data.redirect_url, orderId: res.data.order_id, amount: finalTotal, mode: 'redirect' });
          setTimeout(() => { window.location.href = res.data.redirect_url; }, 1500);
          return;
        }
        setCheckoutModal({ url: res.data.redirect_url, orderId: res.data.order_id, amount: finalTotal, mode: 'popup', popup });
        return;
      }
      setPayError("Impossible d'initialiser le paiement. Réessayez.");
    } catch (err) {
      console.error('Payment error:', err, 'Response:', err?.response?.data);
      let msg = "Une erreur est survenue. Réessayez.";
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0];
        const field = Array.isArray(first?.loc) ? first.loc[first.loc.length - 1] : '';
        msg = `Champ manquant ou invalide : ${field || first?.msg || 'vérifiez le formulaire'}`;
      } else if (err?.message) {
        msg = `Erreur réseau : ${err.message}`;
      }
      setPayError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Popup window polling: detect payment status + popup close, just like
  // PayPal Smart Buttons. Works only when mode === 'popup'.
  useEffect(() => {
    if (!checkoutModal?.orderId || checkoutModal.mode !== 'popup') return;
    let stopped = false;
    const popup = checkoutModal.popup;

    const cleanup = () => {
      stopped = true;
      try { if (popup && !popup.closed) popup.close(); } catch {}
    };

    const poll = async () => {
      if (stopped) return;
      // If the user manually closed the popup, allow a retry
      try {
        if (popup && popup.closed) {
          stopped = true;
          setCheckoutModal(null);
          setPayError('Fenêtre de paiement fermée. Vous pouvez réessayer.');
          return;
        }
      } catch {}
      try {
        const r = await axios.get(`${API}/orders/${checkoutModal.orderId}/public`);
        const ps = (r.data?.payment_status || '').toLowerCase();
        if (stopped) return;
        if (ps === 'paid') {
          cleanup();
          setCheckoutModal(null);
          setOrderPlaced({
            order_id: checkoutModal.orderId,
            customer_name: r.data.customer_name || '',
            customer_email: r.data.customer_email || '',
            total: r.data.total || 0,
          });
          await clearCart();
          return;
        }
        if (ps === 'failed' || ps === 'cancelled' || ps === 'expired') {
          cleanup();
          setCheckoutModal(null);
          setPayError("Le paiement n'a pas abouti. Réessayez.");
          return;
        }
      } catch {}
    };
    const t = setInterval(poll, 2500);
    poll();
    return () => { clearInterval(t); cleanup(); };
  }, [checkoutModal, clearCart]);

  if (orderPlaced) {
    return (
      <div data-testid="order-confirmation" className="pt-28 pb-24 min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <h1 className="font-serif text-4xl sm:text-5xl font-light mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Commande Confirm&eacute;e
          </h1>
          <p className="text-black/50 text-sm mb-2">Commande #{orderPlaced.order_id}</p>
          <p className="text-2xl font-light mb-8">{formatFCFA(orderPlaced.total)}</p>
          <p className="text-sm text-black/50 mb-10">
            Merci, {orderPlaced.customer_name}. Nous enverrons les d&eacute;tails de confirmation &agrave; {orderPlaced.customer_email}.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/shop" className="btn-primary inline-block">Continuer les Achats</Link>
            {customer && <Link to="/account" className="btn-secondary inline-block">Mes Commandes</Link>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="pt-24 sm:pt-28 pb-24">
      {/* Popup / redirect overlay — keeps balmarts.com visible while SenePay
          runs in a top-level context (popup window or full-page redirect) so
          Wave/Orange Money/Free Money/E-Money apps launch natively. */}
      {checkoutModal && (
        <div
          data-testid="senepay-overlay"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ backgroundColor: '#F5F0E8' }}
        >
          <div className="w-full max-w-md text-center">
            <p className="font-serif text-4xl sm:text-5xl font-light tracking-tight mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Balmart
            </p>
            <p className="text-[9px] uppercase tracking-[0.4em] font-bold text-black/40 mb-10">SPEND LESS</p>

            <div className="py-7 border-y border-black/10 mb-8">
              <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-black/40 mb-3">Montant à payer</p>
              <p className="font-serif text-5xl sm:text-6xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {formatFCFA(checkoutModal.amount || 0)}
              </p>
              <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/40 mt-3">
                Commande #{(checkoutModal.orderId || '').replace('ord_', '').slice(0, 8).toUpperCase()}
              </p>
            </div>

            <div className="flex items-center justify-center gap-2 mb-5">
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#B80000', animationDelay: '0ms' }} />
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#B80000', animationDelay: '200ms' }} />
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#B80000', animationDelay: '400ms' }} />
            </div>

            {checkoutModal.mode === 'popup' ? (
              <>
                <p className="text-sm text-black/70 mb-1">Paiement en cours dans la fenêtre</p>
                <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-black/40 mb-8">Wave · Orange Money · Free Money · E-Money</p>
                <button
                  type="button"
                  onClick={() => {
                    try { if (checkoutModal.popup && !checkoutModal.popup.closed) checkoutModal.popup.focus(); } catch {}
                  }}
                  data-testid="senepay-refocus"
                  className="inline-block text-[10px] uppercase tracking-[0.25em] font-bold border-b border-black/20 pb-0.5 text-black/60 hover:text-[#B80000] hover:border-[#B80000] transition-colors mr-6"
                >
                  Afficher la fenêtre
                </button>
                <button
                  type="button"
                  onClick={() => {
                    try { if (checkoutModal.popup && !checkoutModal.popup.closed) checkoutModal.popup.close(); } catch {}
                    setCheckoutModal(null);
                    setPayError('Paiement annulé. Vous pouvez réessayer.');
                  }}
                  data-testid="senepay-cancel"
                  className="inline-block text-[10px] uppercase tracking-[0.25em] font-bold border-b border-black/20 pb-0.5 text-black/40 hover:text-[#B80000] hover:border-[#B80000] transition-colors"
                >
                  Annuler
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-black/70 mb-1">Redirection vers le paiement sécurisé</p>
                <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-black/40">Wave · Orange Money · Free Money · E-Money</p>
                <a
                  href={checkoutModal.url}
                  data-testid="senepay-manual-continue"
                  className="inline-block mt-8 text-[10px] uppercase tracking-[0.25em] font-bold border-b border-black/20 pb-0.5 text-black/50 hover:text-[#B80000] hover:border-[#B80000] transition-colors"
                >
                  Continuer manuellement
                </a>
              </>
            )}
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/shop" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-black/40 hover:text-black transition-colors mb-8">
          <ArrowLeft size={14} /> Continuer les Achats
        </Link>

        <TextScramble
          text="Votre Panier"
          className="text-5xl sm:text-6xl tracking-tighter font-light text-black mb-12"
          delay={0.1}
        />

        {recoveryBanner && (
          <div data-testid="recovery-banner" className="mb-8 p-4 border-l-4 flex items-start gap-3" style={{ borderColor: '#B80000', backgroundColor: 'rgba(184,0,0,0.05)' }}>
            <ShieldCheck size={18} style={{ color: '#B80000' }} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold mb-0.5">Reprenez votre commande où vous l'aviez laissée</p>
              <p className="text-xs text-black/60">Vos informations sont déjà remplies. Finalisez votre paiement en quelques secondes.</p>
            </div>
          </div>
        )}

        {items.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-black/40 text-sm mb-6">Votre panier est vide</p>
            <Link to="/shop" className="btn-primary inline-block">Acheter Maintenant</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-6">
              {items.map(item => (
                <div key={item.product_id} data-testid={`cart-page-item-${item.product_id}`} className="flex gap-4 sm:gap-6 border-b border-black/5 pb-6">
                  <Link to={`/product/${item.product_id}`} className="w-20 h-20 sm:w-32 sm:h-32 bg-[#EDE8DE] flex-shrink-0">
                    <img src={item.product?.image_url} alt={item.product?.name} className="product-image w-full h-full object-cover" />
                  </Link>
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-serif text-base sm:text-lg font-normal leading-tight truncate" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{item.product?.name}</h3>
                        <p className="text-xs sm:text-sm text-black/40 mt-1">Peau {SKIN_TYPE_FR[item.product?.skin_type] || item.product?.skin_type}</p>
                      </div>
                      <span className="font-semibold text-sm sm:text-base whitespace-nowrap">{formatFCFA((item.product?.price || 0) * item.quantity)}</span>
                    </div>
                    <div className="flex items-center justify-between mt-3 sm:mt-4">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <button data-testid={`cart-decrease-${item.product_id}`} onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="w-8 h-8 border border-black/20 flex items-center justify-center hover:bg-black hover:text-white transition-colors">
                          <Minus size={12} />
                        </button>
                        <span className="text-sm font-semibold w-6 text-center" style={{ color: '#B80000' }}>{item.quantity}</span>
                        <button data-testid={`cart-increase-${item.product_id}`} onClick={() => updateQuantity(item.product_id, item.quantity + 1)} className="w-8 h-8 border border-black/20 flex items-center justify-center hover:bg-black hover:text-white transition-colors">
                          <Plus size={12} />
                        </button>
                      </div>
                      <button data-testid={`cart-remove-${item.product_id}`} onClick={() => removeItem(item.product_id)} className="text-black/30 hover:text-black transition-colors p-1">
                        <Trash2 size={16} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Upsell: You might also love */}
              {suggestions.length > 0 && (
                <div data-testid="cart-suggestions" className="pt-6">
                  <div className="flex items-center gap-2 mb-5">
                    <Sparkles size={14} strokeWidth={1.5} style={{ color: '#B80000' }} />
                    <p className="text-xs uppercase tracking-[0.25em] font-bold text-black/60">On aime aussi</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {suggestions.map(p => (
                      <Link
                        key={p.product_id}
                        to={`/product/${p.product_id}`}
                        data-testid={`suggestion-${p.product_id}`}
                        className="group block border border-black/5 hover:border-black/20 transition-colors p-3"
                      >
                        <div className="aspect-square bg-[#EDE8DE] overflow-hidden mb-3">
                          <img
                            src={p.image_url}
                            alt={p.name}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <h4 className="font-serif text-sm font-normal text-black truncate" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                          {p.name}
                        </h4>
                        <p className="text-xs mt-1" style={{ color: '#B80000' }}>{formatFCFA(p.price)}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="lg:sticky lg:top-28 self-start space-y-6">
              {/* Free shipping tracker */}
              {(() => {
                const reached = totalPrice >= FREE_SHIPPING_THRESHOLD;
                const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - totalPrice);
                const percent = Math.min(100, Math.round((totalPrice / FREE_SHIPPING_THRESHOLD) * 100));
                return (
                  <div data-testid="free-shipping-tracker" className="border p-5" style={{ borderColor: reached ? '#B80000' : 'rgba(0,0,0,0.1)', backgroundColor: reached ? 'rgba(184,0,0,0.04)' : 'transparent' }}>
                    <div className="flex items-center gap-3 mb-3">
                      <Truck size={16} strokeWidth={1.5} style={{ color: reached ? '#B80000' : '#000' }} />
                      {reached ? (
                        <p className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: '#B80000' }}>
                          Livraison gratuite débloquée
                        </p>
                      ) : (
                        <p className="text-xs text-black/70">
                          Ajoutez <strong>{formatFCFA(remaining)}</strong> pour la livraison offerte
                        </p>
                      )}
                    </div>
                    <div className="w-full h-1 bg-black/10 overflow-hidden">
                      <div
                        className="h-full transition-all duration-500"
                        style={{ width: `${percent}%`, backgroundColor: '#B80000' }}
                        data-testid="shipping-progress-bar"
                      />
                    </div>
                  </div>
                );
              })()}

              <div className="border border-black/10 p-8">
                <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 mb-6">R&eacute;capitulatif</h3>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-black/60">Sous-total</span>
                  <span className="font-semibold">{formatFCFA(totalPrice)}</span>
                </div>
                <div className="flex justify-between items-center mb-6">
                  <span className="text-sm text-black/60">Livraison</span>
                  <span className="text-sm text-black/40">Calcul&eacute; &agrave; la caisse</span>
                </div>

                {/* Promo code input */}
                <div className="border-t border-black/10 pt-4 mb-4">
                  {appliedPromo ? (
                    <div data-testid="applied-promo" className="flex items-center justify-between p-3 mb-3" style={{ backgroundColor: 'rgba(184,0,0,0.05)', border: '1px solid rgba(184,0,0,0.2)' }}>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#B80000' }}>
                          {appliedPromo.code}
                        </p>
                        <p className="text-[10px] text-black/50 mt-0.5">
                          -{appliedPromo.discount_percent}% appliqu&eacute; &middot; vous &eacute;conomisez {formatFCFA(appliedPromo.discount_amount)}
                        </p>
                      </div>
                      <button
                        data-testid="remove-promo-btn"
                        onClick={removePromo}
                        className="text-xs uppercase tracking-widest text-black/40 hover:text-black"
                      >
                        Retirer
                      </button>
                    </div>
                  ) : (
                    <div className="mb-3">
                      <p className="text-sm font-semibold text-black mt-2 mb-3">Est-ce que vous avez un code promo ?</p>
                      <div className="flex gap-2">
                        <input
                          data-testid="promo-input"
                          type="text"
                          value={promoInput}
                          onChange={e => { setPromoInput(e.target.value.toUpperCase()); setPromoError(''); }}
                          placeholder="Entrez votre code"
                          className="flex-1 bg-transparent border border-black/15 focus:border-black outline-none px-3 py-2 text-sm uppercase tracking-wider"
                        />
                        <button
                          data-testid="apply-promo-btn"
                          onClick={() => applyPromo()}
                          disabled={validatingPromo || !promoInput.trim()}
                          className="px-4 text-xs uppercase tracking-[0.2em] font-bold transition-opacity"
                          style={{ backgroundColor: '#1a1a1a', color: '#fff', opacity: (!promoInput.trim() || validatingPromo) ? 0.4 : 1 }}
                        >
                          {validatingPromo ? '...' : 'Appliquer'}
                        </button>
                      </div>
                      {promoError && (
                        <p data-testid="promo-error" className="text-xs mt-2" style={{ color: '#B80000' }}>{promoError}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Loyalty points redemption */}
                {customer && loyalty && loyalty.points > 0 && (
                  <div data-testid="loyalty-redeem-block" className="border-t border-black/10 pt-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs uppercase tracking-[0.2em] font-bold text-black/40">Points fidélité</p>
                      <p className="text-[10px] text-black/50">
                        {loyalty.points.toLocaleString('fr-FR')} pts · {formatFCFA(loyalty.value_fcfa)}
                      </p>
                    </div>
                    {loyalty.points < (loyalty.min_redeem || 5000) ? (
                      <p data-testid="loyalty-min-info" className="text-[11px] text-black/50">
                        Atteignez {(loyalty.min_redeem || 5000).toLocaleString('fr-FR')} pts pour commencer à les utiliser.
                      </p>
                    ) : pointsToUse > 0 ? (
                      <div data-testid="loyalty-applied" className="flex items-center justify-between p-3" style={{ backgroundColor: 'rgba(184,0,0,0.05)', border: '1px solid rgba(184,0,0,0.2)' }}>
                        <div>
                          <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#B80000' }}>
                            {pointsToUse.toLocaleString('fr-FR')} pts utilisés
                          </p>
                          <p className="text-[10px] text-black/50 mt-0.5">
                            Vous économisez {formatFCFA(pointsDiscount)}
                          </p>
                        </div>
                        <button
                          data-testid="remove-points-btn"
                          type="button"
                          onClick={() => setPointsToUse(0)}
                          className="text-xs uppercase tracking-widest text-black/40 hover:text-black"
                        >
                          Retirer
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2 flex-wrap">
                        <button
                          data-testid="use-points-min-btn"
                          type="button"
                          onClick={() => setPointsToUse(loyalty.min_redeem || 5000)}
                          className="flex-1 min-w-[90px] px-3 py-2 text-[11px] uppercase tracking-[0.15em] font-bold border border-black/20 hover:bg-black hover:text-white transition-colors"
                        >
                          Min · {formatFCFA(Math.floor((loyalty.min_redeem || 5000) * pointValue))}
                        </button>
                        <button
                          data-testid="use-points-half-btn"
                          type="button"
                          onClick={() => {
                            const half = Math.floor(loyalty.points / 2);
                            setPointsToUse(half >= (loyalty.min_redeem || 5000) ? half : (loyalty.min_redeem || 5000));
                          }}
                          className="flex-1 min-w-[90px] px-3 py-2 text-[11px] uppercase tracking-[0.15em] font-bold border border-black/20 hover:bg-black hover:text-white transition-colors"
                        >
                          50%
                        </button>
                        <button
                          data-testid="use-points-all-btn"
                          type="button"
                          onClick={() => setPointsToUse(loyalty.points)}
                          className="flex-1 min-w-[90px] px-3 py-2 text-[11px] uppercase tracking-[0.15em] font-bold border transition-colors"
                          style={{ borderColor: '#B80000', color: '#B80000' }}
                        >
                          Tout utiliser
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="border-t border-black/10 pt-4 mb-8">
                  {appliedPromo && (
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm" style={{ color: '#B80000' }}>Réduction ({appliedPromo.discount_percent}%)</span>
                      <span className="text-sm font-semibold" style={{ color: '#B80000' }}>-{formatFCFA(appliedPromo.discount_amount)}</span>
                    </div>
                  )}
                  {pointsDiscount > 0 && (
                    <div className="flex justify-between items-center mb-2" data-testid="points-discount-row">
                      <span className="text-sm" style={{ color: '#B80000' }}>Points fidélité</span>
                      <span className="text-sm font-semibold" style={{ color: '#B80000' }}>-{formatFCFA(pointsDiscount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center mb-2" data-testid="transaction-fee-row">
                    <span className="text-sm text-black/60">Frais de transaction (1.8%)</span>
                    <span className="text-sm text-black/60">{formatFCFA(transactionFee)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total</span>
                    <span className="text-xl font-light" data-testid="cart-total">{formatFCFA(finalTotal)}</span>
                  </div>
                </div>

                {!showCheckout ? (
                  <button
                    data-testid="proceed-checkout-btn"
                    onClick={() => setShowCheckout(true)}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    Passer la Commande <ArrowRight size={14} />
                  </button>
                ) : (
                  <form data-testid="checkout-form" onSubmit={placeOrder} className="space-y-4">
                    {!customer && (
                      <p data-testid="checkout-guest-note" className="text-[11px] text-black/40 -mt-1 mb-2">
                        Vous commandez en tant qu'invité.{' '}
                        <Link to="/login" className="underline hover:text-black">Se connecter</Link> pour retrouver vos points fidélité.
                      </p>
                    )}
                    <input
                      data-testid="checkout-name"
                      type="text"
                      placeholder="Nom Complet"
                      value={checkoutForm.customer_name}
                      onChange={e => setCheckoutForm({...checkoutForm, customer_name: e.target.value})}
                      required
                      className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors"
                    />
                    <input
                      data-testid="checkout-email"
                      type="email"
                      placeholder="Adresse E-mail"
                      value={checkoutForm.customer_email}
                      onChange={e => setCheckoutForm({...checkoutForm, customer_email: e.target.value})}
                      required
                      className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors"
                    />
                    <textarea
                      data-testid="checkout-address"
                      placeholder="Adresse de Livraison"
                      value={checkoutForm.shipping_address}
                      onChange={e => setCheckoutForm({...checkoutForm, shipping_address: e.target.value})}
                      required
                      rows={2}
                      className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors resize-none"
                    />
                    <input
                      data-testid="checkout-phone"
                      type="tel"
                      placeholder="T&eacute;l&eacute;phone"
                      value={checkoutForm.customer_phone}
                      onChange={e => setCheckoutForm({...checkoutForm, customer_phone: e.target.value})}
                      className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors"
                    />

                    <div data-testid="payment-method-info" className="pt-2">
                      <p className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 mb-3">Paiement sécurisé</p>
                      <div className="flex items-center gap-3 p-3 border border-black/15 bg-[#EDE8DE]/40">
                        <CreditCard size={16} style={{ color: '#B80000' }} />
                        <div className="flex-1">
                          <div className="text-sm font-semibold">SenePay</div>
                          <div className="text-xs text-black/50">Wave · Orange Money · Free Money · E-Money · Carte</div>
                        </div>
                      </div>
                      {senepayReady === false && (
                        <p className="text-[11px] mt-2" style={{ color: '#B80000' }}>
                          Le paiement en ligne est temporairement indisponible.
                        </p>
                      )}
                    </div>

                    {payError && (
                      <p data-testid="pay-error" className="text-xs" style={{ color: '#B80000' }}>{payError}</p>
                    )}

                    <button
                      data-testid="place-order-btn"
                      type="submit"
                      disabled={submitting || senepayReady === false}
                      className="btn-primary w-full"
                    >
                      {submitting ? 'Traitement en cours...' : 'Payer Maintenant'}
                    </button>

                    <div className="pt-4 mt-2 border-t border-black/5">
                      <div className="flex items-center gap-2 mb-2">
                        <ShieldCheck size={14} style={{ color: '#B80000' }} />
                        <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-black/60">Paiement 100% Sécurisé</p>
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] uppercase tracking-[0.1em] text-black/40">
                        <span className="flex items-center gap-1"><Lock size={9} /> SSL 256-bit</span>
                        <span>PCI DSS</span>
                        <span>Conforme BCEAO</span>
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
